import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, Quote, ThumbsUp, Calendar } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSchemaOrg } from '../hooks/useSchemaOrg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ average: 0, total: 0 });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      const activeReviews = response.data.filter(r => r.active !== false);
      setReviews(activeReviews);
      
      // Calculate stats
      if (activeReviews.length > 0) {
        const total = activeReviews.length;
        const sum = activeReviews.reduce((acc, r) => acc + (r.rating || 5), 0);
        setStats({
          average: (sum / total).toFixed(1),
          total: total
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const { name: companyName, SITE_URL } = useSchemaOrg();

  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": companyName,
    "@id": `${SITE_URL}/#organization`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": stats.average,
      "reviewCount": stats.total,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviews.slice(0, 10).map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author || review.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating || 5,
        "bestRating": "5",
        "worstRating": "1"
      },
      "reviewBody": review.content || review.text,
      "datePublished": review.date || review.created_at
    }))
  };

  // Render stars
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster anmeldelser...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Kundeanmeldelser | Sand-tec AS</title>
        <meta name="description" content={`Les hva våre kunder sier om Sand-tec AS. ${stats.total} anmeldelser med gjennomsnittlig ${stats.average} av 5 stjerner.`} />
        <meta name="keywords" content="anmeldelser, omtaler, kundetilbakemeldinger, Sand-tec, verksted" />
        <link rel="canonical" href={`${BACKEND_URL}/anmeldelser`} />
        
        <meta property="og:title" content="Kundeanmeldelser | Sand-tec AS" />
        <meta property="og:description" content={`${stats.total} anmeldelser - ${stats.average}/5 stjerner`} />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #ca8a04', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#ca8a04', margin: '0 0 8px'}}>Kundeanmeldelser — Sand-tec AS</h1>
            <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>Les hva våre kunder sier om Sand-tec AS. Vi er stolte over å levere kvalitet og service som kundene verdsetter.</p>
          </header>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Kundetilfredshet</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Sand-tec AS har en høy kundetilfredshet med gjennomsnittlig 4.8 av 5 stjerner basert på våre kundeanmeldelser. Kundene verdsetter vår profesjonalitet, raske service og konkurransedyktige priser. Vi streber etter å levere den beste opplevelsen for alle som besøker vårt verksted.</p>
          </section>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Hva kundene sier</h2>
            <ul style={{lineHeight: '2', color: '#374151', paddingLeft: '20px'}}>
              <li>Rask og effektiv EU-kontroll for tungbil</li>
              <li>Konkurransedyktige priser på alle tjenester</li>
              <li>God kapasitet — kort ventetid</li>
              <li>Faglig dyktige mekanikere</li>
              <li>Profesjonell og vennlig service</li>
            </ul>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Navigasjon</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#ca8a04'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#ca8a04'}}>Tjenester</a></li>
              <li><a href="/kampanjer" style={{color: '#ca8a04'}}>Kampanjer</a></li>
              <li><a href="/aktuelt" style={{color: '#ca8a04'}}>Aktuelt</a></li>
              <li><a href="/referanser" style={{color: '#ca8a04'}}>Referanser</a></li>
              <li><a href="/kontakt" style={{color: '#ca8a04'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>Kontakt oss for mer informasjon.</p>
            <p style={{marginTop: '5px'}}>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#ca8a04'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#ca8a04'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <div className="relative pt-32 pb-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,179,8,0.1),transparent_50%)]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Kunde
                <span className="bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
                  anmeldelser
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Se hva våre kunder sier om oss. Vi er stolte av å levere kvalitet og service som våre kunder setter pris på.
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-8 mt-8">
              <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-900/20 border border-yellow-600/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(Math.round(parseFloat(stats.average)))}
                </div>
                <p className="text-4xl font-bold text-white">{stats.average}</p>
                <p className="text-yellow-400 text-sm">av 5 stjerner</p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-6">
                <p className="text-4xl font-bold text-white">{stats.total}</p>
                <p className="text-gray-400 text-sm">totalt antall anmeldelser</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-600/20 to-green-900/20 border border-green-600/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-1">
                  <ThumbsUp className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-xl font-bold text-white">Anbefalt</p>
                <p className="text-green-400 text-sm">av våre kunder</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="container mx-auto px-4 pb-20">
          {reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <article
                  key={review.id || review._id || index}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-6 hover:border-yellow-600/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                      {review.avatar || review.image ? (
                        <img
                          src={(review.avatar || review.image).startsWith('http') 
                            ? (review.avatar || review.image) 
                            : `${BACKEND_URL}${review.avatar || review.image}`}
                          alt={review.author || review.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                          {(review.author || review.name || 'A').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-white font-semibold">
                        {review.author || review.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {renderStars(review.rating || 5)}
                        </div>
                        {(review.date || review.created_at) && (
                          <span className="text-gray-500 text-sm flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(review.date || review.created_at).toLocaleDateString('nb-NO')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Review Content */}
                  <div className="relative">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-yellow-600/20" />
                    <p className="text-gray-300 pl-6 leading-relaxed">
                      {review.content || review.text}
                    </p>
                  </div>

                  {/* Service Tag */}
                  {review.service && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <span className="text-xs text-gray-500">Tjeneste:</span>
                      <span className="ml-2 text-sm text-red-400">{review.service}</span>
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Star className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Ingen anmeldelser ennå</h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Vi jobber hardt for å gi våre kunder den beste opplevelsen. 
                Kom tilbake senere for å se hva kundene våre sier.
              </p>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 pb-20">
          <div className="bg-gradient-to-r from-yellow-900/30 to-red-900/30 border border-yellow-600/30 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Har du vært kunde hos oss?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Vi setter stor pris på tilbakemeldinger fra våre kunder. 
              Din mening hjelper oss å bli bedre og hjelper andre med å finne oss.
            </p>
            <a
              href="https://g.page/r/sand-tec-as/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-lg transition-all"
            >
              <Star className="w-5 h-5 mr-2" />
              Skriv en anmeldelse
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReviewsPage;
